import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { BRANDING_CONFIGS } from '@branding/branding.config';
import { BrandingService } from '@branding/branding.service';

import { environment } from '../../../../environments/environment';

const DEV_BRANDING_KEY = 'dev_branding';

/**
 * Ключ бренда по умолчанию. В списке переключателя не показывается: на любом хосте,
 * где запускается дев-сборка, автоопределение по hostname и так даёт этот бренд,
 * поэтому отдельный пункт дублировал бы сброс выбора.
 */
const DEFAULT_BRAND_KEY = 'default';

/**
 * Инструмент разработки: позволяет посмотреть любой бренд из BRANDING_CONFIGS
 * без подмены hostname. В production-сборке полностью отключён, сохранённый
 * выбор в этом случае игнорируется.
 */
@Injectable({
  providedIn: 'root',
})
export class DevBrandingService {
  private readonly brandingService = inject(BrandingService);
  private readonly titleService = inject(Title);

  readonly isAvailable = !environment.production;

  readonly brandKeys = Object.keys(BRANDING_CONFIGS).filter((key) => key !== DEFAULT_BRAND_KEY);

  /** Выбранный вручную бренд, null — бренд определяется по hostname */
  get currentKey(): string | null {
    if (!this.isAvailable) {
      return null;
    }

    const brandKey = localStorage.getItem(DEV_BRANDING_KEY);

    return brandKey && this.brandKeys.includes(brandKey) ? brandKey : null;
  }

  /**
   * Накатывает сохранённый бренд поверх определённого по hostname.
   * Вызывается на старте сразу после BrandingService.updateBranding() и при смене бренда.
   */
  restore(): void {
    const brandKey = this.currentKey;

    if (!brandKey) {
      return;
    }

    const brand = BRANDING_CONFIGS[brandKey];

    this.brandingService.brand = brand;
    this.brandingService.versionInfo = brand.versionInfo;
    this.titleService.setTitle(brand.title);
    this.updateFavicon(brand);
  }

  /**
   * Запоминает выбор и применяет его на месте, без перезагрузки: сначала откатываемся
   * к бренду по hostname, затем накатываем выбранный. Всё брендированное читается
   * компонентами через геттеры, поэтому обновляется на ближайшем цикле change detection.
   */
  apply(brandKey: string | null): void {
    if (!this.isAvailable) {
      return;
    }

    if (brandKey && this.brandKeys.includes(brandKey)) {
      localStorage.setItem(DEV_BRANDING_KEY, brandKey);
    } else {
      localStorage.removeItem(DEV_BRANDING_KEY);
    }

    this.brandingService.updateBranding();
    this.restore();
  }

  private updateFavicon(brand: (typeof BRANDING_CONFIGS)[string]): void {
    const favicon = document.getElementById('favicon') as HTMLLinkElement | null;

    if (favicon) {
      favicon.type = brand.logo.favicon.type === 'svg' ? 'image/svg+xml' : 'image/png';
      favicon.href = brand.logo.favicon.icon;
    }
  }
}
